import type React from "react";

import { Bar, CartesianGrid, BarChart as RechartsBarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "styled-components";

import { ChartTooltipContent, type ChartTooltipContentProps } from "./ChartTooltipContent";

type AxisConfig = {
  dataKey: string;
  tickFormatter?: (value: string) => string;
};

interface BarChartProps {
  color: "blue" | "green";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  name: string;
  xAxis: AxisConfig;
  yAxis: AxisConfig;
  tooltipProps?: ChartTooltipContentProps;
}

export const BarChart: React.FC<BarChartProps> = ({ color, data, name, xAxis, yAxis, tooltipProps }) => {
  const theme = useTheme();

  const colors = {
    blue: { backgroundColor: theme.colors.redesign.b10, color: theme.colors.redesign.b100 },
    green: { backgroundColor: theme.colors.redesign.g10, color: theme.colors.redesign.g100 },
  };

  return (
    <ResponsiveContainer width="100%" height={278}>
      <RechartsBarChart
        data={data}
        barGap={16}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 16,
        }}
      >
        <CartesianGrid vertical={false} stroke={theme.colors.redesign.db20} />
        <XAxis
          dataKey={xAxis.dataKey}
          angle={45}
          dy={12}
          fontSize="14px"
          fontWeight={500}
          interval={0}
          stroke={theme.colors.redesign.db20}
          tick={{ fill: theme.colors.redesign.db80 }}
          tickFormatter={xAxis.tickFormatter}
          tickLine={false}
        />
        <YAxis
          dataKey={yAxis.dataKey}
          axisLine={false}
          domain={[0, 100]}
          fontSize="14px"
          fontWeight={500}
          tick={{ fill: theme.colors.redesign.db80 }}
          tickLine={false}
          width={40}
        />
        <Tooltip
          content={tooltipProps ? <ChartTooltipContent {...tooltipProps} /> : undefined}
          cursor={data && data.length > 0 ? { fill: colors[color].backgroundColor, fillOpacity: 0.5 } : false}
        />
        <Bar dataKey={yAxis.dataKey} fill={colors[color].color} name={name} radius={[10, 10, 0, 0]} />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};
